import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck, UserCheck, MapPinned, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { FadeUp } from "../components/Reveal";
import { INDUSTRY_OPTIONS, TURNOVER_OPTIONS, HELP_OPTIONS } from "../data/content";

import { submitNetlifyForm } from "../lib/netlifyForm";
import { track } from "../lib/track";

const EMPTY = {
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    website: "",
    industry: "",
    annual_turnover: "",
    biggest_challenge: "",
    help_needed: "",
    preferred_time: "",
    privacy_consent: false,
};

const TRUST = [
    { icon: ShieldCheck, text: "Strict 100% confidentiality guarantee" },
    { icon: UserCheck, text: "Direct discussion with a senior commercial advisor — no junior BDMs" },
    { icon: MapPinned, text: "NZ-based, familiar with grocery, wholesale and retail channels" },
];

const NEXT_STEPS = [
    { step: "01", title: "Tell us what needs to improve", desc: "Two minutes on the form — the more honest, the more useful the conversation." },
    { step: "02", title: "We reply within one business day", desc: "Personally, by email or phone, to lock in a time that suits." },
    { step: "03", title: "A practical 30-minute conversation", desc: "Where the business is constrained and what to tackle first. No obligation." },
];


export default function Booking() {
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [botField, setBotField] = useState("");

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await submitNetlifyForm("growth-conversation", {
                ...form,
                privacy_consent: form.privacy_consent ? "Yes" : "No",
                "bot-field": botField,
            });
            setDone(true);
            track("generate_lead", { form: "growth-conversation" });
            toast.success("Request received — we'll be in touch within one business day.");
            window.scrollTo(0, 0);
        } catch (err) {
            toast.error("Something went wrong sending your request. Please try again, or email leo@growvise.co.nz.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="booking-page">
            <Seo
                title="Book a Growth Conversation | GrowVise"
                description="Book a free 30-minute Growth Conversation with GrowVise — practical, confidential commercial advice for NZ product businesses."
                path="/book-a-conversation"
            />

            {/* Header — navy */}
            <section className="bg-navy-950 pt-32 sm:pt-40 pb-14 sm:pb-16">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp>
                        <p className="eyebrow">Book a Conversation</p>
                        <h1 className="mt-6 font-serif font-semibold text-white tracking-tight leading-[1.15] text-4xl sm:text-5xl max-w-2xl" data-testid="booking-title">
                            Book a Growth Conversation.
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-xl">
                            A free, practical 30-minute discussion about where your business is constrained and what to tackle first. No assessment required beforehand.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* Content — cream */}
            <section className="bg-cream section-pad" data-testid="booking-shell">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
                    <FadeUp className="lg:col-span-5">
                        <p className="eyebrow-light">What to expect</p>
                        <div className="mt-8 space-y-7">
                            {NEXT_STEPS.map((s) => (
                                <div key={s.step} className="flex gap-5" data-testid={`booking-step-${s.step}`}>
                                    <p className="font-serif font-semibold text-3xl text-navy-900/15 shrink-0 w-10">{s.step}</p>
                                    <div>
                                        <p className="font-sans font-semibold text-ink">{s.title}</p>
                                        <p className="text-sm text-warmgrey mt-1.5 leading-relaxed">{s.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-10 pt-8 border-t border-navy-900/10 space-y-4">
                            {TRUST.map((t) => (
                                <div key={t.text} className="flex items-start gap-3" data-testid={`trust-${t.text.slice(0, 20).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                    <t.icon size={17} className="text-gold-700 mt-0.5 shrink-0" />
                                    <p className="text-sm text-ink/80">{t.text}</p>
                                </div>
                            ))}
                        </div>
                        <a href="mailto:leo@growvise.co.nz" className="mt-10 inline-flex items-center gap-3 text-sm font-medium text-navy-900 hover:text-gold-700 transition-colors duration-200" data-testid="booking-email-link">
                            <Mail size={16} className="text-gold-700" /> leo@growvise.co.nz
                        </a>
                    </FadeUp>

                    <FadeUp delay={0.1} className="lg:col-span-7">
                        {done ? (
                            <div className="card-light p-10 sm:p-14 text-center" data-testid="booking-success">
                                <CheckCircle2 size={44} className="text-gold-600 mx-auto" />
                                <h2 className="font-serif font-semibold text-3xl text-ink mt-6">Request received.</h2>
                                <p className="mt-4 text-warmgrey leading-relaxed max-w-md mx-auto">
                                    Thanks {form.full_name.split(" ")[0]} — we've got your details and will be in touch within one business day to arrange your Growth Conversation. We'll reply to {form.email}.
                                </p>
                                <Link to="/business-health-check" className="btn-outline-ink mt-8" data-testid="booking-success-hc-btn">
                                    Try the free Business Health Check
                                </Link>
                            </div>
                        ) : (
                            <form
                                name="growth-conversation"
                                method="POST"
                                action="/__forms.html"
                                data-netlify="true"
                                netlify-honeypot="bot-field"
                                onSubmit={submit}
                                className="card-light p-7 sm:p-10 space-y-5"
                                data-testid="booking-form"
                            >
                                <input type="hidden" name="form-name" value="growth-conversation" />
                                <p className="hidden" aria-hidden="true">
                                    <label>Leave this empty: <input name="bot-field" tabIndex={-1} autoComplete="off" value={botField} onChange={(e) => setBotField(e.target.value)} /></label>
                                </p>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="bf-name" className="label-mono">Full name *</label>
                                        <input id="bf-name" required className="input-light" placeholder="Your name" value={form.full_name} onChange={set("full_name")} data-testid="booking-name-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="bf-business" className="label-mono">Business name *</label>
                                        <input id="bf-business" required className="input-light" placeholder="Your company" value={form.business_name} onChange={set("business_name")} data-testid="booking-business-input" />
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="bf-email" className="label-mono">Work email *</label>
                                        <input id="bf-email" type="email" required className="input-light" placeholder="you@company.co.nz" value={form.email} onChange={set("email")} data-testid="booking-email-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="bf-phone" className="label-mono">Phone</label>
                                        <input id="bf-phone" type="tel" className="input-light" placeholder="+64 21 000 000 (optional)" value={form.phone} onChange={set("phone")} data-testid="booking-phone-input" />
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="bf-website" className="label-mono">Website</label>
                                        <input id="bf-website" className="input-light" placeholder="yourcompany.co.nz (optional)" value={form.website} onChange={set("website")} data-testid="booking-website-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="bf-industry" className="label-mono">Industry</label>
                                        <select id="bf-industry" className="input-light" value={form.industry} onChange={set("industry")} data-testid="booking-industry-select">
                                            <option value="">Select…</option>
                                            {INDUSTRY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="bf-turnover" className="label-mono">Approx. annual turnover</label>
                                        <select id="bf-turnover" className="input-light" value={form.annual_turnover} onChange={set("annual_turnover")} data-testid="booking-turnover-select">
                                            <option value="">Select…</option>
                                            {TURNOVER_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="bf-help" className="label-mono">What help is needed</label>
                                        <select id="bf-help" className="input-light" value={form.help_needed} onChange={set("help_needed")} data-testid="booking-help-select">
                                            <option value="">Select…</option>
                                            {HELP_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="bf-challenge" className="label-mono">Main commercial challenge *</label>
                                    <textarea id="bf-challenge" required rows={4} className="input-light resize-none"
                                        placeholder="e.g. Sales are steady but margin keeps slipping, and everything still runs through me…"
                                        value={form.biggest_challenge} onChange={set("biggest_challenge")} data-testid="booking-challenge-textarea" />
                                </div>
                                <div>
                                    <label htmlFor="bf-time" className="label-mono">Preferred time for a call</label>
                                    <select id="bf-time" className="input-light" value={form.preferred_time} onChange={set("preferred_time")} data-testid="booking-time-select">
                                        <option value="">No preference</option>
                                        <option value="Weekday morning">Weekday morning</option>
                                        <option value="Weekday midday">Weekday midday</option>
                                        <option value="Weekday afternoon">Weekday afternoon</option>
                                        <option value="After hours">After hours</option>
                                    </select>
                                </div>
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="booking-consent-label">
                                    <input type="checkbox" required className="mt-1 accent-[#A38020]" checked={form.privacy_consent}
                                        onChange={set("privacy_consent")} data-testid="booking-consent-checkbox" />
                                    <span className="text-xs text-warmgrey leading-relaxed">
                                        I agree to GrowVise storing these details to respond to my enquiry, as described in the{" "}
                                        <Link to="/privacy" className="text-gold-700 hover:text-gold-600 underline underline-offset-2" data-testid="booking-privacy-link">Privacy Policy</Link>. *
                                    </span>
                                </label>
                                <button type="submit" disabled={submitting} className="btn-gold w-full disabled:opacity-50" data-testid="booking-submit-btn">
                                    {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                                    Request Growth Conversation
                                </button>
                                <p className="text-xs text-navy-900/45 leading-relaxed text-center">
                                    Your details are held in strict confidence and never shared with third parties.
                                </p>
                            </form>
                        )}
                    </FadeUp>
                </div>
            </section>
        </div>
    );
}
